import React, { useContext, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import {
    ClassroomAnalytics,
    ModuleDashboard,
    ParamTypes,
    Student,
} from "../../../../interfaces/Dashboard";
import { sessionStore } from "../../../../contexts/SessionContext";
import LockedResourceWarningBanner from "./LockedResourceWarningBanner/LockedResourceWarningBanner";
import SpecimenHelper from "../SpecimenHelper/SpecimenHelper";
import PageWrapper from "../../../../components/PageWrapper/PageWrapper";
import ProgressionTable from "./ProgressionTable/ProgressionTable";
import ProgressionNav from "./ProgressionNav/ProgressionNav";
import "./ProgressionOverview.scss";
import PageHeader from "../../../../components/PageHeader/PageHeader";
import useClassroom from "../Classroom/useClassroom";
import dashboardMessages from "../dashboardMessages";
import { useLockStatus } from "../../../../hooks/useLockStatus__legacy";
import { errorStore } from "../../../../contexts/ErrorContext";
import { useGroup } from "../Group/api/useQueries";
import { pageTitles } from "../../../../utils/messages";
import TabTitle from "../../../../components/TabTitle/TabTitle";
import { useDashboardRouter } from "../routing";
import EmptyContainer from "../../../../design-system-components/WorkmodeLayout/EmptyContainer/EmptyContainer";
import { useImages } from "../../../../hooks/useConfig";
import { defaultImages } from "../../../../utils/defaultImages";
import { LegacyLoader } from "../../../../components/Loader/Loader";
import { useDynamicDashboard } from "../../../../hooks/useDashboard";
interface Props {
    collections: ClassroomAnalytics[];
    classroomsAnalytics?: ClassroomAnalytics[];
}

const ProgressionOverview = ({ collections, classroomsAnalytics }: Props) => {
    const { setErrorInfo } = useContext(errorStore);
    const {
        session: { specimen },
    } = useContext(sessionStore);
    const { moduleId, classroomId, groupId } = useParams<ParamTypes>();
    const intl = useIntl();
    const { getUrl } = useClassroom();
    const history = useHistory();
    const { lockStatus } = useLockStatus({ collectionId: classroomId });
    const { classroomList: classroomListUrl } = useDashboardRouter();
    const images = useImages();
    const collection = useMemo(() => {
        if (classroomId) {
            return collections.find(
                (classroom) => classroom.id === classroomId
            );
        } else {
            return collections.find((group) => group.external_id === groupId);
        }
    }, [classroomId, collections, groupId]);

    const { data: group } = useGroup(groupId, {
        enabled: !!groupId,
    });

    const module = useMemo<ModuleDashboard | undefined>(
        () => collection?.modulesList.find((module) => module.id === moduleId),
        [collection?.modulesList, moduleId]
    );

    const { dashboardLoaded } = useDynamicDashboard();

    const students = useMemo(() => {
        if (classroomId) return module?.students;
        else {
            const _students: { id: string; classroomId: string }[] = [];
            /** @ts-ignore */
            module?.students.forEach((studentId) => {
                const student = group?.students.find(
                    (student) => student.id === studentId
                );
                if (student) {
                    _students.push({
                        id: student.id,
                        classroomId: student.classrooms[0],
                    });
                }
            });

            if (classroomsAnalytics) {
                const studentsAnalytics: Record<string, Student> = {};
                _students.forEach((student) => {
                    const studentClassroom = classroomsAnalytics.find(
                        (classroom) => classroom.id === student.classroomId
                    );
                    if (studentClassroom) {
                        const studentModule = studentClassroom.modulesList.find(
                            (module) => module.id === moduleId
                        );
                        if (studentModule) {
                            studentsAnalytics[student.id] =
                                studentModule.students[student.id];
                        }
                    }
                });

                return studentsAnalytics;
            }
        }
    }, [
        classroomId,
        group?.students,
        module?.students,
        classroomsAnalytics,
        moduleId,
    ]);

    if (dashboardLoaded && !collection) {
        setErrorInfo({
            displayModal: true,
            modal: {
                type: "NOTIFICATION",
                content: {
                    title: intl.formatMessage(
                        dashboardMessages.classroomNotFound
                    ),
                },
            },
        });
        history.push(classroomListUrl);
        return <></>;
    }

    const collectionUrl = classroomId
        ? getUrl("classroom", classroomId)
        : getUrl("group", groupId);

    const title = intl.formatMessage(
        pageTitles.teacherDashboardClassroomProgression
    );

    if (!collection) {
        return (
            <>
                <LegacyLoader />
            </>
        );
    }

    return (
        <>
            <PageWrapper className="progression-overview">
                <TabTitle title={`${collection.name} - ${title}`} />
                {lockStatus && (
                    <LockedResourceWarningBanner
                        moduleId={moduleId}
                        classroomId={classroomId}
                        prLockStatus={lockStatus}
                    />
                )}
                <div className="progression-overview__header-container">
                    <PageHeader
                        title={{
                            content: intl.formatMessage({
                                id: "dashboard-common-progression",
                                defaultMessage: "Progression",
                            }),
                        }}
                        backTo={collectionUrl}
                        className="progression-overview__header"
                    />
                    <ProgressionNav
                        collections={collections}
                        collection={collection}
                        prLockStatus={lockStatus}
                    />
                </div>

                {module && module.progression > 0 && students ? (
                    <ProgressionTable
                        students={students}
                        objectives={module.objectives}
                        prLockStatus={lockStatus}
                        moduleId={moduleId}
                    />
                ) : (
                    <EmptyContainer
                        className="progression-overview__empty"
                        img={
                            images?.dashboard?.teacher?.progressionOverview
                                ?.empty
                        }
                        defaultImg={
                            defaultImages.teacherProgressionOverviewEmpty
                        }
                    />
                )}
                {specimen && (
                    <SpecimenHelper
                        dialogTitle={intl.formatMessage(
                            messages.specimenProgressionOverviewHelperTitle
                        )}
                        dialogDescription={intl.formatMessage(
                            messages.specimenProgressionOverviewHelperDescription
                        )}
                    />
                )}
            </PageWrapper>
        </>
    );
};

const messages = defineMessages({
    noProgressionForModule: {
        id: "dashboard-noProgression",
        defaultMessage: "There is no progression for this module",
    },
    specimenProgressionOverviewHelperTitle: {
        id: "specimen-progression-overview-helper-title",
        defaultMessage: "Class Progression",
    },
    specimenProgressionOverviewHelperDescription: {
        id: "specimen-progression-overview-helper-description",
        defaultMessage: `Here is a global view of the progress of the class, with details by student. The validated objectives are in dark blue, those in progress in light blue.\nBy clicking on a box, you will see the details of the student's progress in the objective.\nBy selecting a student, you will be able to consult his entire history.`,
    },
});

export default ProgressionOverview;
